/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable max-len */
/* eslint-disable no-underscore-dangle */
import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of, Subject } from 'rxjs';
import { ApiAuthService } from '../apis/api-auth.service';
import { ApiMeService } from '../apis/api-me.service';
import { DataStoreService } from './data-store.service';
import { Auth } from '@angular/fire/auth';
import { Company, Project, Site, User } from '../models/base.model';
import { LANG_STORAGE_KEY } from '../transloco/transloco.constants';

@Injectable({
  providedIn: 'root'
})
/**
 * User data provider to hold user specific data like session and cache
 */
export class UserDataService {
  private readonly KeyUser = 'user:profile';
  private readonly KeyCompanyActive = 'company:active';
  /** Survive sign-out: not account data */
  private readonly preservedKeys = [LANG_STORAGE_KEY];
  private readonly KeyProjectActive = 'project:active'; //deprecated
  private readonly KeyProjectSelectList = 'project:selectlist';
  private readonly KeyCompanySelectList = 'company:selectlist';
  private readonly KeyCompanyMemberships = 'company:memberships';
  private readonly KeyCompanySiteSelectList = 'companysite:selectlist';
  private readonly KeySiteSelectList = 'site:selectlist';
  private readonly KeyLastAccessedSite = 'site:last-accessed';
  private readonly KeyFavoriteSites = 'site:favorites';
  private readonly KeyRecentSites = 'site:recent-history';
  private auth: Auth = inject(Auth);

  private user: User;
  private company: Company;
  private companySite: Company;
  private project: Project;
  private site: Site;

  private selectedProjects: Project[];
  private selectedCompanies: Company[];
  private myMemberships: Company[];
  private selectedCompanySites: Company[];
  private selectedSites: Site[];
  private validateCompanyEventSubject = new Subject<boolean>();
  public validateCompanyEvent = this.validateCompanyEventSubject.asObservable();

  constructor(
    private _storage: DataStoreService,
    private apiAuthSvc: ApiAuthService,
    private apiMeSvc: ApiMeService
  ) { }

  async init() {

  }

  hasRole(role: string): boolean {
    const company = this.getCompany();
    return company?.teamRoles?.some(x => x.role === role) ?? false;
  }

  hasRoles(roles: string[]): boolean {
    const company = this.getCompany();
    return company?.teamRoles?.some(teamRole => roles.includes(teamRole.role)) ?? false;
  }

  // memberships (every company and site this user belongs to, with their role)

  /**
   * All companies and sites the user is a member of. Each entry carries the
   * user's own role in that company (`teamRoles`) and its `parent`, which is
   * what permission checks need to tell "my site" from "someone else's".
   */
  getMyMemberships(): Company[] {
    if (!this.myMemberships || this.myMemberships.length === 0) {
      const jsonObj = this._storage.getObject(this.KeyCompanyMemberships);
      this.myMemberships = jsonObj ? jsonObj.map(x => Company.parse(x)) : [];
    }
    return this.myMemberships;
  }

  setMyMemberships(objs: Company[]): boolean {
    this.myMemberships = objs;
    return this._storage.setObject(this.KeyCompanyMemberships, objs.map(x => x.rawJson));
  }

  /** The user's own role (OWN/STA/SUV/STF) in a company, or null if not a member. */
  getMyRoleInCompany(id: number): string | null {
    if (!id) {
      return null;
    }
    const company = this.getMyMemberships().find(x => x.id === id);
    return company?.teamRoles?.[0]?.role ?? null;
  }

  isMemberOfCompany(id: number): boolean {
    return !!id && this.getMyMemberships().some(x => x.id === id);
  }
  // user
  getUser(): User {

    if (!this.user) {
      const jsonObj = this._storage.getObject(this.KeyUser);
      this.user = User.parse(jsonObj);
    }
    return this.user;
  }

  setUser(obj: User): boolean {
    this.user = obj;
    return this._storage.setObject(this.KeyUser, obj.rawJson);
  }

  //company (own)
  getCompany(): Company {
    if (!this.company) {
      const jsonObj = this._storage.getObject(this.KeyCompanyActive);
      if (jsonObj) {
        this.company = Company.parse(jsonObj);
      }
    }

    return this.company;
  }

  setCompany(obj: Company): boolean {
    this.company = obj;
    return this._storage.setObject(this.KeyCompanyActive, obj.rawJson);
  }

  // company
  getSelectedCompany(id: number): Company {
    const data = this.getSelectedCompanies();

    return data.find(x => x.id === id);
  }

  getSelectedCompanies(): Company[] {
    if (!this.selectedCompanies || this.selectedCompanies.length === 0) {
      const jsonObj = this._storage.getObject(this.KeyCompanySelectList);
      if (jsonObj) {
        this.selectedCompanies = jsonObj.map(x => Company.parse(x));
      } else {
        this.selectedCompanies = [];
      }
    }

    return this.selectedCompanies;
  }

  setSelectedCompanies(obj: Company[]) {
    this.selectedCompanies = obj;
    const rawData = obj.map(x => x.rawJson);
    return this._storage.setObject(this.KeyCompanySelectList, rawData);
  }

  addSelectedCompanies(obj: Company): Company[] {
    const data = this.getSelectedCompanies();
    const foundIndex = data.findIndex(x => x.id === obj.id);
    if (foundIndex > -1) {
      data.splice(foundIndex, 1, obj);
    } else {
      data.push(obj);
    }

    this.setSelectedCompanies(data);
    return data;
  }

  //site
  getSelectedCompanySite(id: number): Company {
    if (!this.companySite || this.companySite.id !== id) {
      const data = this.getSelectedCompanySites();

      this.companySite = data.find(x => x.id === id);

    }

    return this.companySite;
  }

  getSelectedCompanySites(): Company[] {
    if (!this.selectedCompanySites || this.selectedCompanySites.length === 0) {
      const jsonObj = this._storage.getObject(this.KeyCompanySiteSelectList);
      if (jsonObj) {
        this.selectedCompanySites = jsonObj.map(x => Company.parse(x));
      } else {
        this.selectedCompanySites = [];
      }
    }

    return this.selectedCompanySites;
  }

  setSelectedCompanySites(obj: Company[]) {
    this.selectedCompanySites = obj;
    const rawData = obj.map(x => x.rawJson);
    return this._storage.setObject(this.KeyCompanySiteSelectList, rawData);
  }

  addSelectedCompanySite(obj: Company): Company[] {
    const data = this.getSelectedCompanySites();
    const foundIndex = data.findIndex(x => x.id === obj.id);
    if (foundIndex > -1) {
      data.splice(foundIndex, 1, obj);
    } else {
      data.push(obj);
    }
    this.setSelectedCompanySites(data);
    this.addToRecentSites(obj);
    return data;
  }

  // project
  getProject(): Project {
    if (!this.project) {
      const jsonObj = this._storage.getObject(this.KeyProjectActive);
      if (jsonObj) {
        this.project = Project.parse(jsonObj);
      }
    }
    return this.project;
  }

  setProject(obj: Project): boolean { //deprecated
    this.project = obj;
    return this._storage.setObject(this.KeyProjectActive, obj.rawJson);
  }

  getSelectedProject(id: number): Project {
    if (!this.project || this.project.id !== id) {
      const data = this.getSelectedProjects();

      this.project = data.find(x => x.id === id);

    }

    return this.project;
  }

  getSelectedProjects(): Project[] {
    if (!this.selectedProjects || this.selectedProjects.length === 0) {
      const jsonObj = this._storage.getObject(this.KeyProjectSelectList);
      if (jsonObj) {
        this.selectedProjects = jsonObj.map(x => Project.parse(x));
      } else {
        this.selectedProjects = [];
      }
    }

    return this.selectedProjects;
  }

  setSelectedProjects(obj: Project[]) {
    this.selectedProjects = obj;
    const rawData = obj.map(x => x.rawJson);
    return this._storage.setObject(this.KeyProjectSelectList, rawData);
  }

  addSelectedProject(obj: Project): Project[] {
    const data = this.getSelectedProjects();
    const foundIndex = data.findIndex(x => x.id === obj.id);
    if (foundIndex > -1) {
      data.splice(foundIndex, 1, obj);
    } else {
      data.push(obj);
    }

    this.setSelectedProjects(data);
    return data;
  }

  //site
  getSelectedSite(id: number): Site {
    if (!this.site || this.site.id !== id) {
      const data = this.getSelectedSites();

      this.site = data.find(x => x.id === id);

    }

    return this.site;
  }

  getSelectedSites(): Site[] {
    if (!this.selectedSites || this.selectedSites.length === 0) {
      const jsonObj = this._storage.getObject(this.KeySiteSelectList);
      if (jsonObj) {
        this.selectedSites = jsonObj.map(x => Site.parse(x));
      } else {
        this.selectedSites = [];
      }
    }

    return this.selectedSites;
  }

  setSelectedSites(obj: Site[]) {
    this.selectedSites = obj;
    const rawData = obj.map(x => x.rawJson);
    return this._storage.setObject(this.KeySiteSelectList, rawData);
  }

  addSelectedSite(obj: Site): Site[] {
    const data = this.getSelectedSites();
    const foundIndex = data.findIndex(x => x.id === obj.id);
    if (foundIndex > -1) {
      data.splice(foundIndex, 1, obj);
    } else {
      data.push(obj);
    }
    this.setSelectedSites(data);

    // Update recent sites history when a site is selected


    return data;
  }

  // Last Accessed Site
  getLastAccessedSite(): Company | null {
    const jsonObj = this._storage.getObject(this.KeyLastAccessedSite);
    if (jsonObj) {
      return Company.parse(jsonObj);
    }
    return null;
  }

  setLastAccessedSite(obj: Company): boolean {
    return this._storage.setObject(this.KeyLastAccessedSite, obj.rawJson);
  }

  clearLastAccessedSite(): void {
    this._storage.remove(this.KeyLastAccessedSite);
  }

  // Favorite Sites
  getFavoriteSites(): Company[] {
    const jsonObj = this._storage.getObject(this.KeyFavoriteSites);
    if (jsonObj && Array.isArray(jsonObj)) {
      return jsonObj.map(x => Company.parse(x));
    }
    return [];
  }

  setFavoriteSites(sites: Company[]): boolean {
    const rawData = sites.map(x => x.rawJson);
    return this._storage.setObject(this.KeyFavoriteSites, rawData);
  }

  addFavoriteSite(obj: Company): Company[] {
    const favorites = this.getFavoriteSites();
    const foundIndex = favorites.findIndex(x => x.id === obj.id);

    if (foundIndex === -1) {
      // Add to favorites if not already present
      favorites.unshift(obj);
      this.setFavoriteSites(favorites);
    }

    return favorites;
  }

  removeFavoriteSite(siteId: number): Company[] {
    const favorites = this.getFavoriteSites();
    const filtered = favorites.filter(x => x.id !== siteId);
    this.setFavoriteSites(filtered);
    return filtered;
  }

  isFavoriteSite(siteId: number): boolean {
    const favorites = this.getFavoriteSites();
    return favorites.some(x => x.id === siteId);
  }

  toggleFavoriteSite(obj: Company): boolean {
    if (this.isFavoriteSite(obj.id)) {
      this.removeFavoriteSite(obj.id);
      return false;
    } else {
      this.addFavoriteSite(obj);
      return true;
    }
  }

  // Recent Sites History
  getRecentSites(limit: number = 5): Company[] {
    const jsonObj = this._storage.getObject(this.KeyRecentSites);
    if (jsonObj && Array.isArray(jsonObj)) {
      const sites = jsonObj.map(x => Company.parse(x));
      return sites.slice(0, limit);
    }
    return [];
  }

  private addToRecentSites(obj: Company): void {
    const recent = this.getRecentSites(20); // Keep last 20 in storage

    // Remove if already exists
    const filtered = recent.filter(x => x.id !== obj.id);

    // Add to beginning
    filtered.unshift(obj);

    // Keep only last 20
    const limited = filtered.slice(0, 20);

    const rawData = limited.map(x => x.rawJson);
    this._storage.setObject(this.KeyRecentSites, rawData);
  }

  clearRecentSites(): void {
    this._storage.remove(this.KeyRecentSites);
  }

  removeRecentSite(obj: Company): Company[] {
    const recent = this.getRecentSites(20);
    const filtered = recent.filter(x => x.id !== obj.id);
    const rawData = filtered.map(x => x.rawJson);
    this._storage.setObject(this.KeyRecentSites, rawData);
    return filtered;
  }


  //api
  apiGetMyProfile(params: any): Observable<User> {
    return this.apiMeSvc.apiGetMe(params).pipe(
      map((res: User) => {
        this.setUser(res);
        return res;
      })
    );
  }

  apiGetCompany(): Observable<Company> {
    const params = {
      page: 1,
      page_size: 200,
      fields: 'id,uuid,title,description,logo_photo,cover_photo,address,type,parent,roles'
    }

    return this.apiMeSvc.apiGetMyCompanies(params).pipe(
      map((res) => {
        // Keep the whole list: permission checks need the user's role in a
        // specific company, not just the first one they happen to belong to.
        this.setMyMemberships(res.data);

        let instance: Company | null = null;
        if (res.data.length > 0) {
          instance = res.data[0];
          this.setCompany(instance);
        }
        return instance;
      })
    );
  }

  /**
   * Memberships from cache, fetching them once if they are not there yet
   * (a page opened directly by URL never went through sign-in this session).
   */
  ensureMyMemberships(): Observable<Company[]> {
    const cached = this.getMyMemberships();
    if (cached && cached.length > 0) {
      return of(cached);
    }

    return this.apiGetCompany().pipe(
      map(() => this.getMyMemberships()),
      catchError((err) => {
        console.error('[UserDataService] Failed to load memberships:', err);
        return of([]);
      })
    );
  }

  // Event
  checkValidateCompany() {
    this.validateCompanyEventSubject.next(true);
  }

  //global
  /**
   * Forget everything about the signed-out account: the in-memory copies and all
   * persisted data (memberships, select lists, vocabularies, filters, ...).
   * Only UI preferences listed in `preservedKeys` survive.
   */
  clearStorage(): void {
    this.user = null;
    this.company = null;
    this.companySite = null;
    this.project = null;
    this.site = null;
    this.selectedProjects = null;
    this.selectedCompanies = null;
    this.myMemberships = null;
    this.selectedCompanySites = null;
    this.selectedSites = null;

    // Clear by exclusion instead of a key list, so newly added caches cannot be forgotten
    this._storage.clearExcept(this.preservedKeys);
  }

}
